import { useState, type ReactNode } from 'react';
import { Header } from '../Header/Header';
import { Sidebar, type SidebarNavGroup, type SidebarRenderNavLinkArgs } from '../Sidebar/Sidebar';

export interface AppShellProps {
  /** アプリ名 */
  appName: string;
  /** ロゴスロット */
  logo?: ReactNode;
  /** サイドバーのナビゲーショングループ */
  navGroups: SidebarNavGroup[];
  /** リンク要素の描画関数（ルーティング実装をアプリ側が注入する） */
  renderNavLink: (args: SidebarRenderNavLinkArgs) => ReactNode;
  /** 現在のパス（アクティブ判定用） */
  currentPath: string;
  /** ヘッダー右側のアクションスロット（テーマトグル等） */
  headerActions?: ReactNode;
  /** ヘッダーのユーザーメニュースロット */
  userMenu?: ReactNode;
  /** サイドバーのフッタースロット */
  sidebarFooter?: ReactNode;
  /** メインコンテンツ（<Outlet /> 等） */
  children: ReactNode;
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business アプリ全体のレイアウトシェル。上部全幅 Header + 下部 Sidebar + main を合成し、
 *  サイドバーの開閉（モバイル）・折りたたみ（デスクトップ）状態を内部管理する。
 *  3 web アプリのルートレイアウトはこのコンポーネントを呼ぶだけにする
 * @graph-connects none
 */
export function AppShell({
  appName,
  logo,
  navGroups,
  renderNavLink,
  currentPath,
  headerActions,
  userMenu,
  sidebarFooter,
  children,
}: AppShellProps): ReactNode {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)]">
      <Header
        appName={appName}
        logo={logo}
        onMenuToggle={() => {
          setIsSidebarOpen(true);
        }}
        actions={headerActions}
        userMenu={userMenu}
      />
      <div className="flex min-h-[calc(100vh-3.5rem)]">
        <Sidebar
          appName={appName}
          logo={logo}
          navGroups={navGroups}
          renderNavLink={renderNavLink}
          currentPath={currentPath}
          footer={sidebarFooter}
          isOpen={isSidebarOpen}
          onClose={() => {
            setIsSidebarOpen(false);
          }}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => {
            setIsSidebarCollapsed((prev) => !prev);
          }}
        />
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="min-w-0 p-4 md:p-6 lg:p-8 animate-content-reveal">{children}</div>
        </main>
      </div>
    </div>
  );
}
